import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';

function NavButton({ onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#9369CC] bg-white text-[#9369CC] shadow-md transition hover:bg-[#9369CC]/10"
    >
      {children}
    </button>
  );
}

export default function CategoryFeaturedCarousel({ courses }) {
  const featuredCourses = useMemo(
    () => courses.filter((course) => course.isSubscriptionTaken),
    [courses]
  );
  const [activeIndex, setActiveIndex] = useState(0);

  if (!featuredCourses.length) return null;

  const current = featuredCourses[activeIndex % featuredCourses.length];
  const completed = current.lessonsCompleted ?? 0;
  const total = current.lessons || 0;
  const progressPercent = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;

  const goPrev = () => {
    setActiveIndex((index) => (index === 0 ? featuredCourses.length - 1 : index - 1));
  };

  const goNext = () => {
    setActiveIndex((index) => (index === featuredCourses.length - 1 ? 0 : index + 1));
  };

  return (
    <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 sm:px-6">
      {featuredCourses.length > 1 && (
        <NavButton onClick={goPrev} label="Previous featured course">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </NavButton>
      )}

      <article className="flex min-h-[220px] flex-1 flex-col overflow-hidden rounded-3xl bg-[#001640] text-white lg:min-h-[260px] lg:flex-row">
        <div className="flex flex-1 flex-col justify-center p-6 sm:p-8">
          <span
            className="inline-flex w-fit rounded-full px-3 py-1 text-xs font-semibold text-white"
            style={{ backgroundColor: current.subjectColorCode || '#F58027' }}
          >
            {current.subject}
          </span>
          <h2 className="mt-4 text-2xl font-bold leading-tight sm:text-3xl lg:text-[32px]">
            {current.title}
          </h2>

          {total > 0 && (
            <div className="mt-5 max-w-md">
              <div className="mb-2 flex items-center justify-between text-sm font-medium">
                <span>Progress</span>
                <span>
                  {completed}/{total}
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-white/20">
                <div
                  className="h-full rounded-full bg-[#FF9C07]"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}

          <Link
            to={ROUTES.COURSE_PLAYLIST(current)}
            className="mt-6 inline-flex w-fit items-center gap-2 rounded-xl bg-gradient-to-r from-[#6A74E1] to-[#B539FD] px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-90 sm:text-base"
          >
            Continue Learning
            <span aria-hidden>→</span>
          </Link>
        </div>

        <div className="relative hidden min-h-[220px] w-[280px] shrink-0 bg-[#0B2B66] lg:block xl:w-[340px]">
          {current.thumbnailUrl ? (
            <img src={current.thumbnailUrl} alt="" className="h-full w-full object-cover opacity-90" />
          ) : (
            <div className={`h-full w-full bg-gradient-to-br ${current.gradient}`} />
          )}
        </div>
      </article>

      {featuredCourses.length > 1 && (
        <NavButton onClick={goNext} label="Next featured course">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </NavButton>
      )}
    </div>
  );
}
